import { zodResolver } from '@hookform/resolvers/zod'
import { ReactNode, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CountrySelect } from '@/forms/components/CountrySelect'
import { UnifiedBusinessSearch } from '@/forms/components/UnifiedBusinessSearch'
import { ParticipantFormSchema } from '@/forms/ParticipantForm/schema'
import { ParticipantFormValues } from '@/forms/ParticipantForm/StandaloneParticipantForm'
import { Participant } from '@/interfaces/Participant'
import API from '@/lib/api'
import { cn } from '@/lib/utils'
import { components } from '@invo.space/shared'

import { editableZoneClassName } from './EditableZone'

const inputClassName =
  'rounded-none border-0 border-b border-dashed px-0 shadow-none focus-visible:ring-0'

const labelClassName = 'text-[10px] text-muted-foreground'

const Field = ({
  label,
  children,
  className,
}: {
  label: string
  children: ReactNode
  className?: string
}) => (
  <div className={cn('space-y-0.5', className)}>
    <span className={labelClassName}>{label}</span>
    {children}
  </div>
)

const schema = z.object({
  supplier: ParticipantFormSchema,
})

export interface InvoicingPreviewSupplierProps {
  participant: Participant | null | undefined
  participantId: string | null | undefined
  onSuccess: () => void
  refSource?: string | null
}

export const InvoicingPreviewSupplier = ({
  participant,
  participantId,
  onSuccess,
  refSource,
}: InvoicingPreviewSupplierProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'settings' })
  const { t: tp } = useTranslation(undefined, { keyPrefix: 'participant' })
  const navigate = useNavigate()

  const methods = useForm<ParticipantFormValues>({
    resolver: zodResolver(schema),
    defaultValues: participant
      ? {
          supplier: {
            ...participant,
            businessId: participant.ico,
            taxId: participant.dic,
          },
        }
      : undefined,
  })

  useEffect(() => {
    if (participant) {
      methods.reset({
        supplier: {
          ...participant,
          businessId: participant.ico,
          taxId: participant.dic,
        },
      })
    }
  }, [participant, methods])

  const handleSave = (values: ParticipantFormValues) => {
    const payload = {
      ...values.supplier,
      ico: values.supplier.businessId,
      dic: values.supplier.taxId,
      businessId: undefined,
      taxId: undefined,
    }

    const saveParticipant = participantId
      ? API.patch<components['schemas']['Participant']>(
          `/api/v2/contacts/${participantId}`,
          payload,
        )
      : API.post<components['schemas']['Participant']>(
          '/api/v2/contacts',
          payload as components['schemas']['Participant'],
        )

    saveParticipant
      .then(({ data }) => {
        if (participantId === null || participantId === undefined) {
          return API.patch('/api/v2/settings', {
            participantId: data.id,
          }).then(() => data)
        }
        return data
      })
      .then(() => {
        toast.success(t('invoicing.supplier.success.title'))
        onSuccess()

        if (refSource === 'invoice') {
          setTimeout(() => navigate('/invoice/new'), 1000)
        }
      })
      .catch(() => {
        toast.error(tp('new.error.title'), {
          description: tp('new.error.description'),
        })
      })
  }

  return (
    <FormProvider {...methods}>
      <div className={editableZoneClassName}>
        <form
          onSubmit={methods.handleSubmit(handleSave)}
          className="space-y-2"
        >
          <h2 className="text-sm font-bold">Dodavatel</h2>

          {!participant && (
            <p className="text-[10pt] text-muted-foreground">
              {t('invoicing.preview.supplier.empty')}
            </p>
          )}

          <Field label={tp('form.nameOrBusinessId.label')}>
            <UnifiedBusinessSearch
              prefix="supplier"
              className={cn(
                inputClassName,
                'h-7 has-[[data-slot=input-group-control]:focus-visible]:ring-0',
              )}
              inputClassName="h-7 px-0 text-sm font-bold"
            />
          </Field>

          <Field label="Ulice a číslo popisné">
            <Input
              className={cn(inputClassName, 'h-6 text-[10pt]')}
              {...methods.register('supplier.address.address')}
            />
          </Field>

          <div className="flex gap-1">
            <Field label="PSČ" className="w-16">
              <Input
                className={cn(inputClassName, 'h-6 w-16 text-[10pt]')}
                {...methods.register('supplier.address.zip')}
              />
            </Field>
            <Field label="Město" className="min-w-0 flex-1">
              <Input
                className={cn(inputClassName, 'h-6 text-[10pt]')}
                {...methods.register('supplier.address.city')}
              />
            </Field>
          </div>

          <Field label="Země">
            <CountrySelect control={methods.control} prefix="supplier" />
          </Field>

          <div className="flex gap-2">
            <Field label={tp('form.businessId.label')} className="min-w-0 flex-1">
              <Input
                className={cn(inputClassName, 'h-6 text-[10pt]')}
                {...methods.register('supplier.businessId')}
              />
            </Field>
            <Field label={tp('form.taxId.label')} className="min-w-0 flex-1">
              <Input
                className={cn(inputClassName, 'h-6 text-[10pt]')}
                {...methods.register('supplier.taxId')}
              />
            </Field>
          </div>

          <Field label={tp('form.email.label')}>
            <Input
              className={cn(inputClassName, 'h-6 text-[10pt]')}
              {...methods.register('supplier.email')}
            />
          </Field>

          <Field label={tp('form.phone.label')}>
            <Input
              className={cn(inputClassName, 'h-6 text-[10pt]')}
              {...methods.register('supplier.phone')}
            />
          </Field>

          <div className="pt-1">
            <Button type="submit" size="sm" className="h-7 text-xs">
              {t('invoicing.preview.supplier.save')}
            </Button>
          </div>
        </form>
      </div>
    </FormProvider>
  )
}
